import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid2';
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';
import { FormikHelpers } from 'formik';
import { Trans } from '@lingui/react';
import { throughputUnitLabelFromThroughputControlType } from 'hooks/useDisplayUnits/display';
import { Form } from 'hooks/useForm';
import {
  BackfillRecipeForm,
  CustomUnitFields,
  ThroughputControlType,
  UnitSystem
} from 'providers/api';
import { round } from 'utils';
import { BINDER_TAILINGS_MARKS } from 'utils/backfill';
import { DictionaryRange } from '../../../MineModel/types';
import UnitSystemNumberField from './UnitSystemNumberField';
import UnitSystemNumberSlider from './UnitSystemNumberSlider';

interface HydraulicTargetInputsProps {
  isOptimised: boolean;
  values: BackfillRecipeForm;
  limits: DictionaryRange;
  isSubmitting: boolean;
  isValid: boolean;
  unitSystemPreference: UnitSystem;
  customUnitFields: CustomUnitFields[];
  throughputControlType: ThroughputControlType;
  handleBlur: (e: any) => void | void;
  setFieldValue: FormikHelpers<BackfillRecipeForm>['setFieldValue'];
  onTargetsUpdate: () => void;
  onReset: () => void;
  hasError: Form['helpers']['hasError'];
  getErrorHelpText: Form['helpers']['getErrorHelpText'];
}

const getThroughputLabel = (throughputControlType: ThroughputControlType) => {
  switch (throughputControlType) {
    case ThroughputControlType.WetTonnage:
      return <Trans id="Wet Tonnage">Wet Tonnage</Trans>;
    case ThroughputControlType.FlowRate:
      return <Trans id="Flow Rate">Flow Rate</Trans>;
    case ThroughputControlType.DryTonnage:
      return <Trans id="Dry Tonnage">Dry Tonnage</Trans>;
    default:
      return null;
  }
};

const HydraulicTargetInputs = ({
  isOptimised,
  values,
  limits,
  isSubmitting,
  isValid,
  unitSystemPreference,
  customUnitFields,
  throughputControlType,
  handleBlur,
  setFieldValue,
  onTargetsUpdate,
  onReset,
  hasError,
  getErrorHelpText,
}: HydraulicTargetInputsProps) => (
  <Grid container spacing={1}>
    <Grid size={12}>
      <Typography variant="caption"><Trans id="Hydraulic Fill">Hydraulic Fill</Trans></Typography>
    </Grid>
    <Grid size={12}>
      <Box>
        <FormHelperText id="recipe-reference-helper-text" component="label">
          <Trans id="Recipe Reference">Recipe Reference</Trans>
        </FormHelperText>
        <TextField
          size="small"
          error={hasError('reference')}
          helperText={
            hasError('reference')
              ? <Trans id={getErrorHelpText('reference')}>{getErrorHelpText('reference')}</Trans>
              : ''
          }
          required
          autoFocus
          fullWidth
          id="reference"
          name="reference"
          aria-labelledby="recipe-reference-helper-text"
          value={values.reference}
          disabled={isOptimised}
          onChange={(e) => setFieldValue('reference', e.target.value)}
          onBlur={handleBlur}
        />
      </Box>
    </Grid>

    {/* Required fields */}

    <Grid size={4}>
      <FormHelperText id="tonnage-helper-text" component="label">
        {getThroughputLabel(throughputControlType)}
      </FormHelperText>
      <UnitSystemNumberField
        unitSystem={unitSystemPreference}
        customUnitFields={customUnitFields}
        unitInfo={throughputUnitLabelFromThroughputControlType(throughputControlType)}
        disabled={throughputControlType !== ThroughputControlType.FlowRate}
        size="small"
        decimalPlaces={0}
        id="tonnage-input"
        name="tonnage-input"
        aria-labelledby="tonnage-helper-text"
        value={values.hydraulicSpecification?.throughput ?? limits.tons.default ?? limits.tons.min}
        onChange={(event) => setFieldValue('hydraulicSpecification.throughput', event.target.value)}
        error={hasError('hydraulicSpecification.throughput')}
        helperText={
          hasError('hydraulicSpecification.throughput')
            ? (
              <Trans id={getErrorHelpText('hydraulicSpecification.throughput')}>
                {getErrorHelpText('hydraulicSpecification.throughput')}
              </Trans>
            )
            : ''
        }
        onBlur={handleBlur}
      />
    </Grid>
    <Grid size={8}>
      <Box
        sx={{
          pt: 2,
          mx: 1,
          px: 1,
        }}
      >
        <UnitSystemNumberSlider
          unitSystem={unitSystemPreference}
          customUnitFields={customUnitFields}
          unitInfo={throughputUnitLabelFromThroughputControlType(throughputControlType)}
          value={values.hydraulicSpecification?.throughput ?? limits.tons.default}
          decimalPlaces={0}
          min={limits.tons.min}
          baseStep={limits.tons.interval}
          max={limits.tons.max}
          onSliderChange={(event: any, newValue: number | number[]) => setFieldValue('hydraulicSpecification.throughput', parseFloat((newValue as number).toFixed(2)))}
          valueLabelDisplay="auto"
          disabled={isOptimised}
          aria-labelledby="tonnage-helper-text"
        />
      </Box>
    </Grid>

    <Grid size={4}>
      <FormHelperText id="adjusted-concentration-helper-text" component="label">
        <Trans id="Binder:Tailings">Binder:Tailings</Trans>
      </FormHelperText>
      <TextField
        size="small"
        disabled
        fullWidth
        id="adjusted-concentration-input"
        name="adjusted-concentration-input"
        value={`1:${round(100 / ((values.hydraulicSpecification?.binderTailingsRatio ?? 0.02) * 100), 0)}`}
        aria-labelledby="adjusted-concentration-helper-text"
      />
    </Grid>

    <Grid size={8}>
      <Box
        sx={{
          mx: 1,
          px: 1,
        }}
      >
        <Slider
          color="secondary"
          value={values.hydraulicSpecification?.binderTailingsRatio ?? 0.02}
          min={0.02}
          step={null}
          max={0.1}
          marks={BINDER_TAILINGS_MARKS}
          onChange={(e, n) => setFieldValue('hydraulicSpecification.binderTailingsRatio', n as number)}
          aria-labelledby="adjusted-concentration-helper-text"
          disabled={isOptimised}
        />
      </Box>
    </Grid>

    <Grid size={12}>
      <Box
        sx={{
          pt: 1,
          pb: 1,
        }}
      >
        <Button
          fullWidth
          variant="contained"
          color="secondary"
          size="large"
          disabled={isSubmitting || !isValid}
          onClick={(e) => {
            e.preventDefault();
            isOptimised ? onReset() : onTargetsUpdate();
          }}
        >
          {isOptimised ? (
            <Trans id="Re-optimise">Re-optimise</Trans>
          ) : (
            <Trans id="Optimise">Optimise</Trans>
          )}
        </Button>
      </Box>
    </Grid>
  </Grid>
);

export default HydraulicTargetInputs;
